/**
 * InvoiceAddDoDialog — attach a Delivery Order to a Sales Control Invoice.
 * Converted 1:1 from Access `frmCtrlSalesQryInvoiceAdd_DOEdit` ("Sale Control -
 * Invoice - DO"). Detail layout (Top order, single left-labelled column):
 *   - invoice_no   (Top=144, Enabled=NotDefault → readonly)  label "Invoice No"
 *   - invoice_date (Top=648, Enabled=NotDefault → readonly)  label "Invoice Date"
 *   - do_no        (Top=1152, ComboBox RowSource `SELECT do_no
 *                   FROM public_v_invoice_lst_do`)           label "DO No"
 *   - subform frmCtrlSalesQryInvoiceAdd_DO (Top=1548): datasheet list of DOs
 *     already attached (public_tmp_invoice_do) + Delete button. No FE listing
 *     endpoint is provided, so the attached-DO list is not rendered here — the
 *     parent invoice grid refreshes via onSaved after each add.
 *
 * btnSave (CodeBehindForm): inserts/reactivates a public_invoice_scs_details row
 * (invoice_no, invoice_date, delivery_order), warns if the pair already exists,
 * then clears do_no and requeries. Web mirror → useAddSaleInvoiceDoMutation
 * (POST /sale-invoices/{id}/add-do). After a successful add we clear the DO
 * field and keep the dialog open (Access `Me.do_no.value = ""`), letting the
 * user attach several DOs in a row; onSaved refreshes the parent.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import dayjs from 'dayjs'

import {
  type InvLookupItem,
  useAddSaleInvoiceDoMutation,
  useInvLookupDeliveryOrdersQuery,
} from '../../store/api/saleInvoiceApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { FORM_GRID_2COL, TEXT_TRUNCATE, HIDE_ON_MOBILE_INLINE } from '../../theme/responsive'

interface Props {
  open: boolean
  invoiceId: number | null
  row: { customsInvNo: string | null; invoiceDate: string | null } | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}

/** Access invoice_date is Format="Short Date" → display DD-MMM-YYYY (readonly). */
function fmtDate(v: string | null): string {
  if (!v) return ''
  const d = dayjs(v)
  return d.isValid() ? d.format('DD-MMM-YYYY') : String(v)
}

export default function InvoiceAddDoDialog({ open, invoiceId, row, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [doNo, setDoNo] = useState('')

  const invoiceNo = row?.customsInvNo ?? ''
  const invoiceDate = row?.invoiceDate ?? null

  const [q, setQ] = useState('')
  const doList = useInvLookupDeliveryOrdersQuery(q || undefined, { skip: !open })
  const [addDo, { isLoading: saving }] = useAddSaleInvoiceDoMutation()

  // Reset field each time the dialog opens.
  useEffect(() => {
    if (open) { setDoNo(''); setQ('') }
  }, [open])

  const options = doList.data?.items ?? []
  // Inject current value into options so the chip renders even before list load.
  const sel: InvLookupItem | null = doNo
    ? options.find((o) => o.value === doNo) ?? { value: doNo, label: doNo }
    : null
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options

  const doNoEmpty = !doNo.trim()
  const canSave = canEdit && !!invoiceId && !!invoiceNo && !doNoEmpty && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave || !invoiceId) return
    try {
      await addDo({
        id: invoiceId,
        body: { customsInvNo: invoiceNo, invoiceDate: invoiceDate || null, doNo },
      }).unwrap()
      snack.success(`Delivery Order ${doNo} added to invoice`)
      onSaved()
      // Access clears do_no and keeps the dialog open to attach more DOs.
      setDoNo('')
      setQ('')
    } catch (err) {
      snack.error(getErrorMessage(err, 'Add delivery order failed'))
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography
          component="span"
          variant="subtitle1"
          noWrap
          title="Sale Control - Invoice - DO"
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Sale Control - </Box>
          Invoice - DO
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to add delivery orders.</Alert>}
        {/* Access Detail: single column of left-labelled rows (invoice_no,
            invoice_date readonly; do_no combo). Web uses a 2-col responsive grid
            with each field on its own row to keep the compact dialog tidy. */}
        <Box sx={{ ...FORM_GRID_2COL, columnGap: 1.5, rowGap: 1.25, mt: 0.5 }}>
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <TextField
              label="Invoice No" size={SM} fullWidth value={invoiceNo}
              InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
              inputProps={{ tabIndex: -1 }}
            />
          </Box>
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <TextField
              label="Invoice Date" size={SM} fullWidth value={fmtDate(invoiceDate)}
              InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
              inputProps={{ tabIndex: -1 }}
            />
          </Box>
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Autocomplete
              size={SM}
              options={opts}
              value={sel}
              loading={doList.isFetching}
              onChange={(_e, v) => setDoNo(v?.value ?? '')}
              onInputChange={(_e, v, reason) => { if (reason === 'input') setQ(v) }}
              filterOptions={(x) => x}
              // RowSource selects only do_no → value === label → collapse to code only.
              getOptionLabel={(o) => (!o ? '' : o.value === o.label ? String(o.value) : `${o.value} — ${o.label}`)}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="DO No *"
                  error={doNoEmpty}
                  inputProps={{ ...params.inputProps, tabIndex: 1 }}
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
